import React, {useCallback, useEffect, useRef, useState} from 'react';
import TokenExpirationModal from './TokenExpirationModal';
import useTokenExpirationCheck from '../../hooks/auth/useTokenExpirationCheck';
import useUserActivity from '../../authentication/useUserActivity';
import useAuthUserActions from '../../hooks/auth/useAuthUserActions';
import {useAuthenticationClient} from "../../authentication/useAuthenticationClient";
import {useAppSelector} from "../../redux/hooks";
import {PortalUserAuthenticationScheme} from "../../redux/types";

const TokenExpirationHandler = () => {
  const user = useAppSelector(state => state.auth.authenticatedUser)

  if (user?.authScheme === PortalUserAuthenticationScheme.Cappy) {
    console.log('[TokenExpiration] User is logged in using Cappy, using CappyTokenExpirationHandler')
    return <CappyTokenExpirationHandler/>
  } else {
    console.log('[TokenExpiration] User is not logged in using Cappy, not using CappyTokenExpirationHandler')
    return null
  }
}

const CappyTokenExpirationHandler: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);

  // Auth and activity hooks
  const {logout, exchange} = useAuthUserActions();
  const isActive = useUserActivity(5 * 60 * 1000);

  // Token status
  const apiTokenCheck = useTokenExpirationCheck(() => localStorage.getItem('authToken'));
  const refreshTokenCheck = useTokenExpirationCheck(() => localStorage.getItem('cappyRefreshToken'));

  const authClient = useAuthenticationClient()

  const isRefreshing = useRef(false);

  const refresh = useCallback(async () => {
    if (isRefreshing.current) {
      return
    }

    console.log('[TokenExpiration] Refreshing tokens');
    const refreshToken = localStorage.getItem('cappyRefreshToken') || ''

    try {
      isRefreshing.current = true;
      const refreshResponse = await authClient.refresh(refreshToken)
      localStorage.setItem('cappyRefreshToken', refreshResponse.refreshToken)
      await exchange(refreshResponse.token)
    } catch (error) {
      console.error('[TokenExpiration] Token refresh failed:', error)
    } finally {
      isRefreshing.current = false;
    }
  }, [authClient, exchange])

  // Recurring token check every 30 seconds
  useEffect(() => {
    const checkTokens = () => {
      const apiTokenStatus = apiTokenCheck();
      const refreshTokenStatus = refreshTokenCheck()

      if (refreshTokenStatus.isExpired) {
        console.warn('[TokenExpiration] Refresh token is expired, logging out');
        logout();
        return;
      }

      if (modalVisible) {
        // If the modal is visible, we await a decision from the user before refreshing.
        return
      }

      if (apiTokenStatus.isExpired || apiTokenStatus.isNearExpiration || refreshTokenStatus.isNearExpiration) {
        if (isActive) {
          refresh()
        }
        else {
          setModalVisible(true);
        }
      }
    };

    // Set up the interval
    const intervalId = setInterval(checkTokens, 30000);

    // Clean up the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, [
    apiTokenCheck,
    refreshTokenCheck,
    isActive,
    logout,
    modalVisible
  ]);

  const onCancelClick = () => {
    setModalVisible(false)
    logout()
  }

  const onOkClick = () => {
    setModalVisible(false)
    refresh()
  }

  return (
    <TokenExpirationModal
      visible={modalVisible}
      onOkClick={onOkClick}
      onCancelClick={onCancelClick}
    />
  );
};

export default TokenExpirationHandler;
